import React, { PropsWithChildren, useContext, useEffect } from 'react';
import * as R from 'ramda';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import ReactMarkdown from 'react-markdown';
import SVGCaretLeft from '../../assets/svg/icons/SVGCaretLeft';
import SVGDot from '../../assets/svg/icons/SVGDot';
import SVGFaceScan from '../../assets/svg/icons/SVGFaceScan';
import Button, { ButtonStyle } from '../Button/Button';
import ButtonContainer from '../Button/ButtonContainer';
import Container from '../Container/Container';
import Loading from '../Loading/Loading';
import TitleContainer from '../TitleContainer/TitleContainer';
import { FlowConfigContext } from '../../../providers/FlowConfigContext';
import UserInfo from '../UserInfo/UserInfo';
import MessagePanelProps, {
  MessagePanelSubDescription,
  MessagePanelSubDescriptionAlignment,
  MessagePanelSubDescriptionIcon,
} from './MessagePanelProps';
import { Routes } from '../../../config/routes';

export default function MessagePanel(props: PropsWithChildren<MessagePanelProps>): React.ReactElement {
  const {
    title,
    description,
    subDescriptions,
    bottomDescription,
    buttonLabel,
    showLoading,
    loading,
    translationVariables,
    onConfirm,
    dataTestId,
    showUserInfo,
    showSupportLink,
    showBack,
    onBack,
    supportLink,
  } = props;

  const { networkName, getExternalLinksConfig } = useContext(FlowConfigContext);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { civicSupportLink } = getExternalLinksConfig();

  const handleConfirm = () => {
    if (onConfirm) {
      onConfirm();
    }
  };

  const handleBackButtonClick = () => {
    if (onBack) {
      onBack();
    }
  };

  const handleContactSupport = () => {
    navigate(Routes.SUPPORT, { state: { from: location.pathname } });
  };

  const mapSubDescriptionIcon = (subDescriptionIcon: MessagePanelSubDescriptionIcon) => {
    switch (subDescriptionIcon) {
      case MessagePanelSubDescriptionIcon.FACE_AUTH:
        return <SVGFaceScan />;
      case MessagePanelSubDescriptionIcon.DOT:
        return <SVGDot />;
      case MessagePanelSubDescriptionIcon.DEFAULT:
        return null;
      default:
        return null;
    }
  };

  const mapSubDescriptionAlignment = (subDescription: MessagePanelSubDescription) => {
    switch (subDescription.alignment) {
      case MessagePanelSubDescriptionAlignment.CENTER:
        return 'text-center';
      case MessagePanelSubDescriptionAlignment.RIGHT:
        return 'text-right';
      case MessagePanelSubDescriptionAlignment.LEFT:
      case MessagePanelSubDescriptionAlignment.DEFAULT:
      default:
        return 'text-left';
    }
  };
  return (
    <Container>
      <>
        {showBack && (
          <div className="flex-basis-0 items-left w-full">
            <Button
              style={ButtonStyle.BACK}
              onClick={handleBackButtonClick}
              disabled={false}
              extraClasses="back-button"
              dataTestid="BACK_BUTTON"
            >
              <div className="flex text-textTertiary uppercase text-xs items-center">
                <SVGCaretLeft />
                <span className="ml-2">{t('common.explainer.backButtonLabel')}</span>
              </div>
            </Button>
          </div>
        )}

        <TitleContainer center dataTestId={dataTestId}>
          <h1 className="mb-4" data-testid={R.is(Array, title) ? R.last(title as string[]) : title}>
            {t(title, { context: networkName, ...translationVariables })}
          </h1>
          <span
            className="mb-4 p pass-status-text"
            data-testid={R.is(Array, description) ? R.last(description as string[]) : description}
          >
            <ReactMarkdown linkTarget="_blank" className="whitespace-pre-line">
              {t(description, { context: networkName, ...translationVariables })}
            </ReactMarkdown>
          </span>

          <div>
            {subDescriptions?.map((subDescription) => {
              return (
                <div key={subDescription.title}>
                  <div className="flex">
                    {subDescription.icon && (
                      <div className="flex-shrink-0 pr-2 justify-self-end">
                        {mapSubDescriptionIcon(subDescription.icon)}
                      </div>
                    )}

                    <div
                      className={`leading-6 text-left text-textParagraph pass-status-text ${mapSubDescriptionAlignment(
                        subDescription,
                      )}`}
                      data-testid={subDescription.title}
                    >
                      {t(subDescription.title, { context: networkName, ...translationVariables })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </TitleContainer>
        {bottomDescription && (
          <div className="flex">
            <div
              className="flex-auto text-lg text-center leading-6 text-textParagraph mb-6 mt-2"
              data-testid={bottomDescription}
            >
              {t(bottomDescription, { context: networkName, ...translationVariables })}
            </div>
          </div>
        )}

        {buttonLabel && (
          <ButtonContainer bottomMargin="mb-0">
            <Button
              style={ButtonStyle.DEFAULT}
              disabled={loading || false}
              loading={loading || false}
              dataTestid="OK_BUTTON"
              onClick={handleConfirm}
            >
              <span>{t(buttonLabel, { context: networkName })}</span>
            </Button>
          </ButtonContainer>
        )}

        {showLoading && (
          <div className="mb-8">
            <Loading />
          </div>
        )}
        {showSupportLink && (
          <span className="w-full mt-6 whitespace-pre-wrap textDetail text-center">
            {t('common.support.supportLinkQuestions')}
            <a
              target="_blank"
              title="Help Center"
              href={supportLink || civicSupportLink}
              className="textLinkDetail"
              rel="noreferrer"
            >
              {t('common.support.supportLinkVisitHelpCenter')}
            </a>
            {t('common.support.supportLinkOr')}
            <button
              type="button"
              className="buttonLink"
              title="Contact support"
              onClick={handleContactSupport}
              data-testid="CONTACT_SUPPORT_BUTTON"
            >
              {t('common.support.supportLinkContactSupport')}
            </button>
          </span>
        )}
        {showUserInfo && <UserInfo />}
      </>
    </Container>
  );
}
MessagePanel.defaultProps = {
  // eslint-disable-next-line react/default-props-match-prop-types
  translationVariables: {},
};
